import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Cookie } from 'lucide-react';

// Fonction pour initialiser Google Consent Mode v2
const initConsentMode = (granted = false) => {
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  
  if (granted) {
    gtag('consent', 'update', {
      'ad_storage': 'granted',
      'ad_user_data': 'granted',
      'ad_personalization': 'granted',
      'analytics_storage': 'granted',
      'functionality_storage': 'granted',
      'personalization_storage': 'granted',
      'security_storage': 'granted'
    });
  } else {
    gtag('consent', 'update', {
      'ad_storage': 'denied',
      'ad_user_data': 'denied',
      'ad_personalization': 'denied',
      'analytics_storage': 'denied',
      'functionality_storage': 'denied',
      'personalization_storage': 'denied',
      'security_storage': 'granted'
    });
  }
};

// Initialiser le mode consent par défaut (tout refusé)
const initDefaultConsent = () => {
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  
  gtag('consent', 'default', {
    'ad_storage': 'denied',
    'ad_user_data': 'denied',
    'ad_personalization': 'denied',
    'analytics_storage': 'denied',
    'functionality_storage': 'denied',
    'personalization_storage': 'denied',
    'security_storage': 'granted',
    'wait_for_update': 500
  });
};

export const CookieBanner = () => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(false);
  const isSpanish = location.pathname.startsWith('/es');
  
  const privacyLink = isSpanish ? '/es/privacidad' : '/fr/confidentialite';

  useEffect(() => {
    // Initialiser le consent mode par défaut (tout refusé)
    initDefaultConsent();
    
    // Vérifier si l'utilisateur a déjà donné son consentement
    const consentStatus = localStorage.getItem('savea-consent-status');
    
    if (!consentStatus) {
      // Afficher la bannière après un court délai
      setTimeout(() => {
        setIsVisible(true);
      }, 1500);
    } else if (consentStatus === 'accepted') {
      // Réactiver le consent si déjà accepté
      initConsentMode(true);
    }
  }, []);

  const acceptAll = () => {
    localStorage.setItem('savea-consent-status', 'accepted');
    localStorage.setItem('savea-consent-date', new Date().toISOString());
    initConsentMode(true);
    setIsVisible(false);
  };

  const rejectAll = () => {
    localStorage.setItem('savea-consent-status', 'rejected');
    localStorage.setItem('savea-consent-date', new Date().toISOString());
    initConsentMode(false);
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-20 left-3 right-3 z-40 max-w-md mx-auto md:bottom-4">
      <div className="bg-white rounded-xl shadow-xl border border-gray-100 p-3">
        <div className="flex items-start gap-2">
          <Cookie className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-gray-600 text-xs leading-tight mb-2">
              {isSpanish 
                ? 'Usamos cookies para personalización de anuncios y análisis.'
                : 'Nous utilisons des cookies pour la personnalisation des annonces et l\'analyse.'
              }
              {' '}
              <a href={privacyLink} className="text-emerald-600 hover:underline">
                {isSpanish ? 'Privacidad' : 'Confidentialité'}
              </a>
              {' • '}
              <a href="https://business.safety.google/privacy/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                Google
              </a>
            </p>
            <div className="flex gap-2">
              <Button 
                onClick={acceptAll}
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs py-1 px-3 h-7"
              >
                {isSpanish ? 'Acceptar' : 'Accepter'}
              </Button>
              <Button 
                onClick={rejectAll}
                variant="outline"
                size="sm" 
                className="border-gray-300 text-gray-600 hover:bg-gray-50 text-xs py-1 px-3 h-7"
              >
                {isSpanish ? 'Rechazar' : 'Refuser'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
